import { TValue } from "../global.types";
import {
    ROUTE_ABSENCE_CATEGORIES,
    ROUTE_ACCOUNT_ASSIGNMENT,
    ROUTE_ATTENDANCE,
    ROUTE_BANK_ACCOUNTS,
    ROUTE_BANK_STATEMENTS,
    ROUTE_BANK_TRANSACTIONS,
    ROUTE_BUSINESS_PARTNER,
    ROUTE_CASH_BOXES,
    ROUTE_CASH_RECEIPTS,
    ROUTE_CASH_RECEIPTS_ISSUED,
    ROUTE_CASH_RECEIPTS_RECEIVED,
    ROUTE_CATEGORIES,
    ROUTE_CHARTS_OF_ACCOUNTS,
    ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES,
    ROUTE_COMPANIES,
    ROUTE_COMPANY_ROLES,
    ROUTE_CORRECTIVE_INVOICE_ISSUED,
    ROUTE_CORRECTIVE_INVOICE_RECEIVED,
    ROUTE_CUSTOMER_PORTAL_PAYMENT,
    ROUTE_DELETE_LOG,
    ROUTE_DIP_COMPANIES,
    ROUTE_DOCUMENT_ITEM_TEMPLATES,
    ROUTE_ELECTRONIC_SUBMISSION,
    ROUTE_EMPLOYEES,
    ROUTE_EMPLOYMENT,
    ROUTE_EMPLOYMENT_TEMPLATE,
    ROUTE_EXTRA_SALARIES,
    ROUTE_FISCAL_YEAR,
    ROUTE_FIXED_ASSET,
    ROUTE_GROUP_DEDUCTIONS,
    ROUTE_HEALTH_INSURANCE_COMPANIES,
    ROUTE_INBOX,
    ROUTE_INDIVIDUAL_DEDUCTIONS,
    ROUTE_INTEGRATIONS,
    ROUTE_INTERNAL_DOCUMENT,
    ROUTE_INVOICE_ISSUED,
    ROUTE_INVOICE_RECEIVED,
    ROUTE_LABEL_HIERARCHIES,
    ROUTE_LIFE_INSURANCE_COMPANIES,
    ROUTE_LOAN_ACCOUNTS,
    ROUTE_LOAN_STATEMENTS,
    ROUTE_LOAN_TRANSACTIONS,
    ROUTE_MESSAGES,
    ROUTE_MINOR_ASSET,
    ROUTE_NUMBER_RANGES,
    ROUTE_OTHER_LIABILITY,
    ROUTE_OTHER_RECEIVABLE,
    ROUTE_PAYSLIP,
    ROUTE_PENSION_INSURANCE_COMPANIES,
    ROUTE_PROFORMA_INVOICE_ISSUED,
    ROUTE_PROFORMA_INVOICE_RECEIVED,
    ROUTE_RECURRING_TASKS,
    ROUTE_SALARY_ADVANCES,
    ROUTE_SALARY_COMPONENTS,
    ROUTE_SYSTEM_ROLES,
    ROUTE_TAXPAYER_DECLARATIONS,
    ROUTE_TICKETS,
    ROUTE_UNORGANIZED_ASSET,
    ROUTE_USERS,
    ROUTE_VAT,
    ROUTE_WORKING_PATTERNS
} from "../routes";
import BindingContext from "./BindingContext";
import { EntitySetName, EntityTypeName, ICompanyEntity } from "./GeneratedEntityTypes";
import { DocumentTypeCode, ElectronicSubmissionTypeCode } from "./GeneratedEnums";

export interface ICompany extends ICompanyEntity {
    CanAccessCustomerPortal?: boolean;
}

export const getEntitySetByDocumentType = (documentType: DocumentTypeCode): EntitySetName => {
    switch (documentType) {
        case DocumentTypeCode.InvoiceReceived:
            return EntitySetName.InvoicesReceived;
        case DocumentTypeCode.InvoiceIssued:
            return EntitySetName.InvoicesIssued;
        case DocumentTypeCode.ProformaInvoiceReceived:
            return EntitySetName.ProformaInvoicesReceived;
        case DocumentTypeCode.ProformaInvoiceIssued:
            return EntitySetName.ProformaInvoicesIssued;
        case DocumentTypeCode.InternalDocument:
            return EntitySetName.InternalDocuments;
        case DocumentTypeCode.OtherLiability:
            return EntitySetName.OtherLiabilities;
        case DocumentTypeCode.OtherReceivable:
            return EntitySetName.OtherReceivables;
        case DocumentTypeCode.CorrectiveInvoiceIssued:
            return EntitySetName.CorrectiveInvoicesIssued;
        case DocumentTypeCode.CorrectiveInvoiceReceived:
            return EntitySetName.CorrectiveInvoicesReceived;
        case DocumentTypeCode.BankTransaction:
            return EntitySetName.BankTransactions;
        case DocumentTypeCode.CashReceiptReceived:
            return EntitySetName.CashReceiptsReceived;
        case DocumentTypeCode.CashReceiptIssued:
            return EntitySetName.CashReceiptsIssued;
        default:
            // not every DocumentType has entitySet
            return null;
    }
};

export const getRouteByDocumentType = (documentType: DocumentTypeCode): string => {
    switch (documentType) {
        case DocumentTypeCode.InvoiceReceived:
            return ROUTE_INVOICE_RECEIVED;
        case DocumentTypeCode.InvoiceIssued:
            return ROUTE_INVOICE_ISSUED;
        case DocumentTypeCode.ProformaInvoiceReceived:
            return ROUTE_PROFORMA_INVOICE_RECEIVED;
        case DocumentTypeCode.ProformaInvoiceIssued:
            return ROUTE_PROFORMA_INVOICE_ISSUED;
        case DocumentTypeCode.InternalDocument:
            return ROUTE_INTERNAL_DOCUMENT;
        case DocumentTypeCode.OtherLiability:
            return ROUTE_OTHER_LIABILITY;
        case DocumentTypeCode.OtherReceivable:
            return ROUTE_OTHER_RECEIVABLE;
        case DocumentTypeCode.CorrectiveInvoiceReceived:
            return ROUTE_CORRECTIVE_INVOICE_RECEIVED;
        case DocumentTypeCode.CorrectiveInvoiceIssued:
            return ROUTE_CORRECTIVE_INVOICE_ISSUED;
        case DocumentTypeCode.BankTransaction:
            return ROUTE_BANK_TRANSACTIONS;
        case DocumentTypeCode.CashReceiptReceived:
            return ROUTE_CASH_RECEIPTS_RECEIVED;
        case DocumentTypeCode.CashReceiptIssued:
            return ROUTE_CASH_RECEIPTS_ISSUED;
        case DocumentTypeCode.LoanTransaction:
            return ROUTE_LOAN_TRANSACTIONS;
    }
};

export const getDocumentTypeCodeFromEntityType = (entityType: EntityTypeName): DocumentTypeCode => {
    switch (entityType) {
        case EntityTypeName.InternalDocument:
            return DocumentTypeCode.InternalDocument;
        case EntityTypeName.OtherReceivable:
            return DocumentTypeCode.OtherReceivable;
        case EntityTypeName.OtherLiability:
            return DocumentTypeCode.OtherLiability;
        case EntityTypeName.ProformaInvoiceIssued:
            return DocumentTypeCode.ProformaInvoiceIssued;
        case EntityTypeName.ProformaInvoiceReceived:
            return DocumentTypeCode.ProformaInvoiceReceived;
        case EntityTypeName.InvoiceIssued:
            return DocumentTypeCode.InvoiceIssued;
        case EntityTypeName.InvoiceReceived:
            return DocumentTypeCode.InvoiceReceived;
        case EntityTypeName.CorrectiveInvoiceIssued:
            return DocumentTypeCode.CorrectiveInvoiceIssued;
        case EntityTypeName.CorrectiveInvoiceReceived:
            return DocumentTypeCode.CorrectiveInvoiceReceived;
        case EntityTypeName.BankTransaction:
            return DocumentTypeCode.BankTransaction;
        case EntityTypeName.LoanTransaction:
            return DocumentTypeCode.LoanTransaction;
        case EntityTypeName.CashReceiptReceived:
            return DocumentTypeCode.CashReceiptReceived;
        case EntityTypeName.CashReceiptIssued:
            return DocumentTypeCode.CashReceiptIssued;
        case EntityTypeName.LoanTransaction:
            return DocumentTypeCode.LoanTransaction;

        default:
            return null;
    }
};

export const getEntityTypeFromDocumentType = (docType: DocumentTypeCode): EntityTypeName => {
    switch (docType) {
        case DocumentTypeCode.InternalDocument:
            return EntityTypeName.InternalDocument;
        case DocumentTypeCode.OtherReceivable:
            return EntityTypeName.OtherReceivable;
        case DocumentTypeCode.OtherLiability:
            return EntityTypeName.OtherLiability;
        case DocumentTypeCode.ProformaInvoiceIssued:
            return EntityTypeName.ProformaInvoiceIssued;
        case DocumentTypeCode.ProformaInvoiceReceived:
            return EntityTypeName.ProformaInvoiceReceived;
        case DocumentTypeCode.InvoiceIssued:
            return EntityTypeName.InvoiceIssued;
        case DocumentTypeCode.InvoiceReceived:
            return EntityTypeName.InvoiceReceived;
        case DocumentTypeCode.CorrectiveInvoiceIssued:
            return EntityTypeName.CorrectiveInvoiceIssued;
        case DocumentTypeCode.CorrectiveInvoiceReceived:
            return EntityTypeName.CorrectiveInvoiceReceived;
        case DocumentTypeCode.BankTransaction:
            return EntityTypeName.BankTransaction;
        case DocumentTypeCode.CashReceiptReceived:
            return EntityTypeName.CashReceiptReceived;
        case DocumentTypeCode.CashReceiptIssued:
            return EntityTypeName.CashReceiptIssued;
        case DocumentTypeCode.LoanTransaction:
            return EntityTypeName.LoanTransaction;

        default:
            return null;
    }
};

export const getRouteByEntityType = (entityType: EntityTypeName): string => {
    const docType = getDocumentTypeCodeFromEntityType(entityType);

    if (docType) {
        return getRouteByDocumentType(docType);
    } else {
        switch (entityType) {
            case EntityTypeName.Asset:
                return ROUTE_FIXED_ASSET;
            case EntityTypeName.MinorAsset:
                return ROUTE_MINOR_ASSET;
            case EntityTypeName.BankStatement:
                return ROUTE_BANK_STATEMENTS;
            case EntityTypeName.BankTransaction:
                return ROUTE_BANK_TRANSACTIONS;
            case EntityTypeName.BusinessPartner:
                return ROUTE_BUSINESS_PARTNER;
            case EntityTypeName.FiscalYear:
                return ROUTE_FISCAL_YEAR;
            case EntityTypeName.User:
                return ROUTE_USERS;
            case EntityTypeName.Account:
                return ROUTE_CHARTS_OF_ACCOUNTS;
            case EntityTypeName.AccountAssignment:
                return ROUTE_ACCOUNT_ASSIGNMENT;
            case EntityTypeName.AccountTemplate:
                return ROUTE_CHARTS_OF_ACCOUNTS_TEMPLATES;
            case EntityTypeName.CashBox:
                return ROUTE_CASH_BOXES;
            case EntityTypeName.CashReceipt:
                return ROUTE_CASH_RECEIPTS;
            case EntityTypeName.CbaCategory:
                return ROUTE_CATEGORIES;
            case EntityTypeName.CommunicationEntry:
                return ROUTE_MESSAGES;
            case EntityTypeName.Company:
                return ROUTE_COMPANIES;
            case EntityTypeName.CompanyBankAccount:
                return ROUTE_BANK_ACCOUNTS;
            case EntityTypeName.CompanyRole:
                return ROUTE_COMPANY_ROLES;
            case EntityTypeName.CurrencyUsedByCompany:
                return "";
            case EntityTypeName.CustomerPortalPayment:
                return ROUTE_CUSTOMER_PORTAL_PAYMENT;
            case EntityTypeName.DocumentDeleteLog:
                return ROUTE_DELETE_LOG;
            case EntityTypeName.GeneralRole:
                return ROUTE_SYSTEM_ROLES;
            case EntityTypeName.InboxFile:
                return ROUTE_INBOX;
            case EntityTypeName.Label:
                return ROUTE_LABEL_HIERARCHIES;
            case EntityTypeName.LabelHierarchy:
                return ROUTE_LABEL_HIERARCHIES;
            case EntityTypeName.NumberRangeDefinition:
                return ROUTE_NUMBER_RANGES;
            case EntityTypeName.PrAbsenceCategory:
                return ROUTE_ABSENCE_CATEGORIES;
            case EntityTypeName.PrAttendance:
                return ROUTE_ATTENDANCE;
            case EntityTypeName.PrEmployee:
                return ROUTE_EMPLOYEES;
            case EntityTypeName.PrEmployment:
                return ROUTE_EMPLOYMENT;
            case EntityTypeName.PrEmploymentTemplate:
                return ROUTE_EMPLOYMENT_TEMPLATE;
            case EntityTypeName.PrExtraSalary:
                return ROUTE_EXTRA_SALARIES;
            case EntityTypeName.PrHealthInsuranceCompany:
                return ROUTE_HEALTH_INSURANCE_COMPANIES;
            case EntityTypeName.PrLifeInsuranceCompany:
                return ROUTE_LIFE_INSURANCE_COMPANIES;
            case EntityTypeName.PrDipCompany:
                return ROUTE_DIP_COMPANIES;
            case EntityTypeName.PrPayrollSetting:
                return "";
            case EntityTypeName.PrPensionInsuranceCompany:
                return ROUTE_PENSION_INSURANCE_COMPANIES;
            case EntityTypeName.PrSalaryAdvance:
                return ROUTE_SALARY_ADVANCES;
            case EntityTypeName.PrSalaryComponent:
                return ROUTE_SALARY_COMPONENTS;
            case EntityTypeName.PrWorkingPattern:
                return ROUTE_WORKING_PATTERNS;
            case EntityTypeName.RecurringTask:
                return ROUTE_RECURRING_TASKS;
            case EntityTypeName.Ticket:
                return ROUTE_TICKETS;
            case EntityTypeName.UnorganizedAsset:
                return ROUTE_UNORGANIZED_ASSET;
            case EntityTypeName.VatClassification:
                return ROUTE_VAT;
            case EntityTypeName.PrGroupDeduction:
                return ROUTE_GROUP_DEDUCTIONS;
            case EntityTypeName.PrIndividualDeduction:
                return ROUTE_INDIVIDUAL_DEDUCTIONS;
            case EntityTypeName.ItemTemplate:
                return ROUTE_DOCUMENT_ITEM_TEMPLATES;
            case EntityTypeName.PrTaxpayerDeclaration:
                return ROUTE_TAXPAYER_DECLARATIONS;
            case EntityTypeName.InvoiceServiceIntegration:
                return ROUTE_INTEGRATIONS;
            case EntityTypeName.PrPayslip:
                return ROUTE_PAYSLIP;
            case EntityTypeName.LoanStatement:
                return ROUTE_LOAN_STATEMENTS;
            case EntityTypeName.LoanAccount:
                return ROUTE_LOAN_ACCOUNTS;
            case EntityTypeName.LoanTransaction:
                return ROUTE_LOAN_TRANSACTIONS;
            default:
                return null;
        }
    }
};

export const getDetailRouteByEntityType = (entityType: EntityTypeName, id: TValue, readOnly?: boolean): string => {
    switch (entityType) {
        // todo: CorrectiveInvoiceIssued, CorrectiveInvoiceReceived
        default:
            const route = getRouteByEntityType(entityType as EntityTypeName);
            return route && `${route}/${id}${readOnly ? "?viewMode=readonly" : ""}`;
    }
};

export function getEntityTypeFromBindingContext(bindingContext: BindingContext): EntityTypeName {
    return bindingContext.getEntityType().getName() as EntityTypeName;
}

export function getSubmissionDetailRoute(year: number, type?: ElectronicSubmissionTypeCode, month?: number): string {
    if (type) {
        return `${ROUTE_ELECTRONIC_SUBMISSION}/${year}/${type}/${month}`;
    } else {
        return `${ROUTE_ELECTRONIC_SUBMISSION}/${year}`;
    }
}

